



































































































































import { GOOGLE_MAPS_API_KEY } from "../../utils/Constants";
import { Component, Mixins, Ref, Watch } from "vue-property-decorator";
import Company from "@/models/Company";
import VSignPost from "@/components/Company/VSignPost.vue";
import CompanyMixin from "@/mixins/http/CompanyMixin";
import VMiniLogo from "@/components/VMiniLogo.vue";
import Sidebar from "@/components/Analysis/SideBar.vue";
import { authStore, currentScoutingStore } from "@/store/typed";
import { AnalysisState, ScoutingAnalysis } from "@/models/Scouting";
import VCardDock from "@/components/VCardDock.vue";
import VResumeeCard, { ResumeeItem } from "@/components/VResumeeCard.vue";
import formatNumber from "@/filters/FormatNumber";
import PopulationCard from "@/components/Analysis/PopulationCard.vue";
import CoverageCard from "@/components/Analysis/CoverageCard.vue";
import MediumCard, {
  MediumCardGroup,
  MediumCardItem,
} from "@/components/Analysis/MediumCard.vue";
import { MediumType } from "@/models";
import VMap from "@/components/Scouting/Map.vue";
import MediumTypeService from "@/service/MediumTypeService";
import ScoutingMediumService from "@/service/ScoutingMediumService";
import StoreSubscriber from "@/utils/StoreSubscriber";
import NumberFormatter from "@/service/NumberFormatter";
import jsFileDownloader from "js-file-downloader";
import { BASE_API_URL } from "@/utils/Constants";
import GroupDeleteModal from "@/components/Analysis/GroupDeleteModal.vue";
import ScoutingGroup from "@/models/ScoutingGroup";
import ScoutingGroupMixin from "@/mixins/http/ScoutingGroupMixin";
import SlidesGenerationModal from "@/components/Analysis/SlidesGenerationModal.vue";

declare global {
  interface Window {
    google?: any;
  }
}

@Component({
  components: {
    VMiniLogo,
    Sidebar,
    VCardDock,
    VResumeeCard,
    PopulationCard,
    CoverageCard,
    MediumCard,
    VMap,
    GroupDeleteModal,
    SlidesGenerationModal,
  },
})
export default class Analysis extends Mixins(CompanyMixin, ScoutingGroupMixin) {
  analysis: ScoutingAnalysis | null = null;
  company: Company | null = null;
  googleMapsLoaded = false;
  @Ref("scoutingMap") readonly map!: VMap;
  @Ref("groupDeleteModal") readonly groupDeleteModal!: GroupDeleteModal;
  @Ref("slidesGenerationModal")
  readonly slidesGeneration!: SlidesGenerationModal;
  mediumTypes: MediumType[] = [];
  mediumTypesNational: MediumType[] = [];
  mediumTypesLocal: MediumType[] = [];

  regionTotal = 0;
  districtTotal = 0;
  townTotal = 0;

  get exportDataUrl() {
    return `${BASE_API_URL}scoutings/analyses/${currentScoutingStore.scoutingAnalysis?.scouting?.id}/export.xlsx`;
  }

  logoPath(value: Company) {
    return VSignPost.removeHeadingBackSlash(value.croppedLogo || "");
  }

  get scriptUrl() {
    return `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&libraries=places&language=it`;
  }

  subscribe() {
    StoreSubscriber.subscribe(
      "currentScouting/$changeScoutingAnalysisState",
      this.onScoutingAnalysisStateChange.bind(this)
    );
    StoreSubscriber.subscribe(
      "currentScouting/$changeScoutingAnalysisCurrentPlace",
      this.onScoutingAnalysisStateChange.bind(this)
    );
    StoreSubscriber.subscribe(
      "currentScouting/$changeScoutingAnalysisCurrentArea",
      this.onScoutingAnalysisStateChange.bind(this)
    );
    StoreSubscriber.subscribe(
      "currentScouting/$changeScoutingAnalysisCurrentGroup",
      this.onScoutingAnalysisStateChange.bind(this)
    );
  }

  async onScoutingAnalysisStateChange() {
    if (this.hasMap) {
      if (!this.map) {
        return;
      }

      this.map.clearMarkers();

      if (this.hasStatusNational) {
        this.addScoutingPlacesToMap();
        this.addScoutingAreasToMap();
        this.map.centerAllScoutingObjects();
      }
      if (this.hasStatusPOI) {
        this.addScoutingPlaceToMap();
        this.map.centerOnPlace(
          currentScoutingStore.analysisCurrentScoutingPlace!
        );
      }
      if (this.hasStatusArea) {
        this.addScoutingAreaToMap();
        this.map.centerOnArea(
          currentScoutingStore.analysisCurrentScoutingArea!
        );
      }
      if (this.hasStatusGroup) {
        this.addScoutingGroupToMap();
        this.map.centerOnGroupArea(
          currentScoutingStore.analysisCurrentScoutingGroup!
        );
      }
    }
  }

  async loadGoogleMaps() {
    if (!window.google?.maps?.places) {
      const plugin = document.createElement("script");
      plugin.setAttribute("src", this.scriptUrl);
      plugin.async = true;
      document.head.appendChild(plugin);
      await new Promise((resolve) => (plugin.onload = resolve));
    }
    this.googleMapsLoaded = true;
  }

  get budgetItems(): ResumeeItem[] {
    const media = this.analysis?.scouting.scoutingMedia?.length;
    const budget = this.analysis?.scouting.scoutingMedia?.reduceRight(
      (acc, medium) => {
        return acc + parseInt(medium.budget) * medium.quantity;
      },
      0
    );
    return [
      {
        description: "Mezzi",
        text: `${media}`,
        icon: "media-tv",
      },
      {
        description: "Budget",
        text: `${formatNumber(budget)} €`,
        icon: "budget",
      },
    ];
  }

  get hasStatusBrief() {
    return this.analysis?.status === AnalysisState.Brief;
  }

  get hasStatusNational() {
    return this.analysis?.status === AnalysisState.National;
  }

  get hasStatusPOI() {
    return (
      this.analysis?.status === AnalysisState.POI &&
      !!currentScoutingStore.analysisCurrentScoutingPlace
    );
  }

  get hasStatusArea() {
    return (
      this.analysis?.status === AnalysisState.Area &&
      !!currentScoutingStore.analysisCurrentScoutingArea
    );
  }

  get hasStatusGroup() {
    return (
      this.analysis?.status === AnalysisState.Group &&
      !!currentScoutingStore.analysisCurrentScoutingGroup
    );
  }

  get companyId() {
    return this.$route.params.id;
  }

  get analysisId() {
    return this.$route.params.analysisId;
  }

  get hasMap() {
    return !this.hasStatusBrief;
  }

  @Watch("hasMap")
  onHasMapChanged(value: boolean) {}

  get cardsTitle() {
    if (this.analysis?.status === AnalysisState.Brief) {
      return "Riepilogo analisi";
    } else if (this.analysis?.status === AnalysisState.National) {
      return "Mezzi nazionali";
    } else if (this.analysis?.status === AnalysisState.Area) {
      return `${currentScoutingStore.analysisCurrentScoutingArea?.name} (Area)`;
    } else if (this.analysis?.status === AnalysisState.POI) {
      return `${currentScoutingStore.analysisCurrentScoutingPlace?.name} (POI)`;
    } else if (this.analysis?.status === AnalysisState.Group) {
      return `${currentScoutingStore.analysisCurrentScoutingGroup?.name} (Gruppo)`;
    }
    return "";
  }

  get regionCoverage() {
    return this.analysis?.scouting.metadata?.regions;
  }

  get districtCoverage() {
    return this.analysis?.scouting.metadata?.districts;
  }

  get townCoverage() {
    return this.analysis?.scouting.metadata?.towns;
  }

  currentSourceableTownCoverage() {
    if (this.hasStatusArea) {
      return currentScoutingStore.analysisCurrentScoutingArea?.metadata?.towns;
    } else if (this.hasStatusPOI) {
      return currentScoutingStore.analysisCurrentScoutingPlace?.metadata?.towns;
    } else if (this.hasStatusGroup) {
      return currentScoutingStore.analysisCurrentScoutingGroup?.metadata?.towns;
    }
  }

  currentSourceableDistrictCoverage() {
    if (this.hasStatusArea) {
      return currentScoutingStore.analysisCurrentScoutingArea?.metadata
        ?.districts;
    } else if (this.hasStatusPOI) {
      return currentScoutingStore.analysisCurrentScoutingPlace?.metadata
        ?.districts;
    } else if (this.hasStatusGroup) {
      return currentScoutingStore.analysisCurrentScoutingGroup?.metadata
        ?.districts;
    }
  }

  currentSourceableRegionCoverage() {
    if (this.hasStatusArea) {
      return currentScoutingStore.analysisCurrentScoutingArea?.metadata
        ?.regions;
    } else if (this.hasStatusPOI) {
      return currentScoutingStore.analysisCurrentScoutingPlace?.metadata
        ?.regions;
    } else if (this.hasStatusGroup) {
      return currentScoutingStore.analysisCurrentScoutingGroup?.metadata
        ?.regions;
    }
  }

  get populations() {
    return this.analysis?.scouting.metadata?.populations;
  }

  currentSourceablePopulations() {
    if (this.hasStatusArea) {
      return currentScoutingStore.analysisCurrentScoutingArea?.metadata
        ?.populations;
    } else if (this.hasStatusPOI) {
      return currentScoutingStore.analysisCurrentScoutingPlace?.metadata
        ?.populations;
    } else if (this.hasStatusGroup) {
      return currentScoutingStore.analysisCurrentScoutingGroup?.metadata
        ?.populations;
    }
  }

  get families() {
    return this.analysis?.scouting.metadata?.families;
  }

  currentSourceableFamilies() {
    if (this.hasStatusArea) {
      return currentScoutingStore.analysisCurrentScoutingArea?.metadata
        ?.families;
    } else if (this.hasStatusPOI) {
      return currentScoutingStore.analysisCurrentScoutingPlace?.metadata
        ?.families;
    } else if (this.hasStatusGroup) {
      return currentScoutingStore.analysisCurrentScoutingGroup?.metadata
        ?.families;
    }
  }

  get populationGroups() {
    return [
      {
        title: "Popolazione residente per classi di età",
        items:
          currentScoutingStore.scoutingAnalysis?.scouting?.metadata?.agePopulations?.map(
            (agePopulation) => {
              return {
                type: "pair",
                key: agePopulation.label,
                value: agePopulation.value,
              };
            }
          ),
      },
    ];
  }

  currentSourceablePopulationGroups() {
    let currentSourceable = null;
    if (this.hasStatusPOI)
      currentSourceable = currentScoutingStore.analysisCurrentScoutingPlace;
    if (this.hasStatusArea)
      currentSourceable = currentScoutingStore.analysisCurrentScoutingArea;
    if (this.hasStatusGroup)
      currentSourceable = currentScoutingStore.analysisCurrentScoutingGroup;
    if (!currentSourceable)
      currentSourceable = currentScoutingStore.scoutingAnalysis?.scouting;

    const results = [
      {
        title: "Popolazione Residente per classe di età",
        items: currentSourceable?.metadata?.agePopulations?.map(
          (agePopulation) => {
            return {
              type: "pair",
              key: agePopulation.label,
              value: this.formatValueWithPercentage(
                agePopulation.value,
                this.populations
              ),
            };
          }
        ),
      },
      {
        title: "Popolazione Residente per classe di età (Custom)",
        items: currentSourceable?.metadata?.youngAgePopulations?.map(
          (youngAgePopulation) => {
            return {
              type: "pair",
              key: youngAgePopulation.label,
              value: this.formatValueWithPercentage(
                youngAgePopulation.value,
                this.populations
              ),
            };
          }
        ),
      },
      {
        title: "Nuclei Familiari per Numero di Componenti",
        items: currentSourceable?.metadata?.familyPopulations?.map(
          (familyPopulation) => {
            return {
              type: "pair",
              key: familyPopulation.label,
              value: this.formatValueWithPercentage(
                familyPopulation.value,
                this.families
              ),
            };
          }
        ),
      },
      {
        title: "Popolazione Residente Equivalente",
        items: [
          {
            type: "scalar",
            value: currentSourceable?.metadata?.equivalentPopulations,
          },
        ],
      },
      {
        title: "Popolazione Residente per Condizione e Posizione Professionale",
        items: currentSourceable?.metadata?.jobPopulations?.map(
          (jobPopulation) => {
            return {
              type: "pair",
              key: jobPopulation.label,
              value: this.formatValueWithPercentage(
                jobPopulation.value,
                this.populations
              ),
            };
          }
        ),
      },
      {
        title: "Popolazione Residente per Scolarità",
        items: currentSourceable?.metadata?.educationPopulations?.map(
          (educationPopulation) => {
            return {
              type: "pair",
              key: educationPopulation.label,
              value: this.formatValueWithPercentage(
                educationPopulation.value,
                this.populations
              ),
            };
          }
        ),
      },
      {
        title: "Consumi Lordi Non Alimentari per Macro Categoria",
        items: currentSourceable?.metadata?.consumptionPopulations?.map(
          (consumptionPopulation) => {
            return {
              type: "pair",
              key: consumptionPopulation.label,
              value: `${NumberFormatter.format(consumptionPopulation.value)} €`,
            };
          }
        ),
      },
      {
        title: "Unità Locali per Comparto Economico",
        items: currentSourceable?.metadata?.localUnitPopulations?.map(
          (localUnitPopulation) => {
            return {
              type: "pair",
              key: localUnitPopulation.label,
              value: `${NumberFormatter.format(localUnitPopulation.value)}`,
            };
          }
        ),
      },
      {
        title: "Consumi Lordi Non Alimentari per Macro-Categoria",
        items: currentSourceable?.metadata?.consumptionPopulations?.map(
          (consumptionPopulation) => {
            return {
              type: "pair",
              key: consumptionPopulation.label,
              value: consumptionPopulation.value,
            };
          }
        ),
      },
      {
        title: "Popolazione Residente per Classi di età custom",
        items: currentSourceable?.metadata?.youngAgePopulations?.map(
          (youngAgePopulation) => {
            return {
              type: "pair",
              key: youngAgePopulation.label,
              value: youngAgePopulation.value,
            };
          }
        ),
      },
      {
        title: "Indice di Densità Commerciale Negozi e Servizi",
        items: [
          {
            type: "scalar",
            value: currentSourceable?.metadata?.commercialDensity,
          },
        ],
      },
    ];
    return results;
  }

  formatValueWithPercentage(value: number, total: number | undefined) {
    if (!total) return NumberFormatter.format(value);
    const percentage = (value / total) * 100;
    return `${NumberFormatter.format(value)} (${NumberFormatter.format(
      percentage
    )} %)`;
  }

  get mediumItems(): MediumCardGroup[] {
    const groupDictionary: {
      [key: string]: { mediumType: MediumType; budget: number; count: number };
    } = {};
    (this.analysis?.scouting.scoutingMedia || []).forEach((medium) => {
      const entry = groupDictionary[medium.medium.mediumType.id] || {
        mediumType: medium.medium.mediumType,
        count: 0,
        budget: 0,
      };
      entry.count += 1;
      entry.budget += parseInt(medium.budget) * medium.quantity;
      groupDictionary[medium.medium.mediumType.id] = entry;
    });
    const nationalGroup: MediumCardGroup = {
      id: "national",
      name: "Nazionali",
      items: [],
    };
    const localGroup: MediumCardGroup = {
      id: "local",
      name: "Locali",
      items: [],
    };
    Object.values(groupDictionary).map((value) => {
      const item: MediumCardItem = {
        id: value.mediumType.id,
        icon: `media-${value.mediumType.typology}`,
        text: value.mediumType.name,
        budget: value.budget,
        count: value.count,
      };
      if (value.mediumType.reach === "national") {
        nationalGroup.items.push(item);
      } else {
        localGroup.items.push(item);
      }
    });
    return [nationalGroup, localGroup];
  }

  get mediumTypeNationalItems(): MediumCardGroup[] {
    const groupDictionary: {
      [key: string]: { mediumType: MediumType; budget: number; count: number };
    } = {};
    this.mediumTypesNational.forEach((mediumType) => {
      const entry = groupDictionary[mediumType.id] || {
        mediumType: mediumType,
        count: 0,
        budget: 0,
      };
      groupDictionary[mediumType.id] = entry;
    });
    this.analysis?.scouting.scoutingMedia?.forEach((scoutingMedium) => {
      if (
        (scoutingMedium.medium.mediumType.reach === "national",
        scoutingMedium.sourceableType === "Scouting")
      ) {
        groupDictionary[scoutingMedium.medium.mediumType.id].count += 1;
        groupDictionary[scoutingMedium.medium.mediumType.id].budget +=
          parseInt(scoutingMedium.budget) * scoutingMedium.quantity;
      }
    });
    const nationalGroup: MediumCardGroup = {
      id: "national",
      name: "Nazionali",
      items: [],
    };
    Object.values(groupDictionary).map((value) => {
      const item: MediumCardItem = {
        id: value.mediumType.id,
        icon: `media-${value.mediumType.typology}`,
        text: value.mediumType.name,
        budget: value.budget,
        count: value.count,
      };
      nationalGroup.items.push(item);
    });
    return [nationalGroup];
  }

  mediumTypeLocalItems(): MediumCardGroup[] {
    const groupDictionary: {
      [key: string]: { mediumType: MediumType; budget: number; count: number };
    } = {};
    this.mediumTypesLocal.forEach((mediumType) => {
      const entry = groupDictionary[`${mediumType.id}`] || {
        mediumType: mediumType,
        count: 0,
        budget: 0,
      };
      groupDictionary[mediumType.id] = entry;
    });

    let sourceableType = "";
    let sourceableId = "";

    if (this.hasStatusArea) {
      sourceableType = "ScoutingArea";
      sourceableId = currentScoutingStore.analysisCurrentScoutingArea!.id;
    } else if (this.hasStatusPOI) {
      sourceableType = "ScoutingPlace";
      sourceableId = currentScoutingStore.analysisCurrentScoutingPlace!.id;
    } else if (this.hasStatusGroup) {
      sourceableType = "ScoutingGroup";
      sourceableId = currentScoutingStore.analysisCurrentScoutingGroup!.id;
    }

    this.analysis?.scouting.scoutingMedia?.forEach((scoutingMedium) => {
      if (
        scoutingMedium.medium.mediumType.reach == "local" &&
        scoutingMedium.sourceableType == sourceableType &&
        scoutingMedium.sourceableId == sourceableId
      ) {
        groupDictionary[scoutingMedium.medium.mediumType.id].count += 1;
        groupDictionary[scoutingMedium.medium.mediumType.id].budget +=
          parseInt(scoutingMedium.budget) * scoutingMedium.quantity;
      }
    });
    const localGroup: MediumCardGroup = {
      id: "local",
      name: "Locali",
      items: [],
    };
    Object.values(groupDictionary).map((value) => {
      const item: MediumCardItem = {
        id: value.mediumType.id,
        icon: `media-${value.mediumType.typology}`,
        text: value.mediumType.name,
        budget: value.budget,
        count: value.count,
      };
      localGroup.items.push(item);
    });
    return [localGroup];
  }

  backToCompany() {
    this.$router.push({
      name: "company_details",
      params: { id: this.companyId },
    });
  }

  async mounted() {
    await this.loadGoogleMaps();
    this.subscribe();
    this.mediumTypes = await MediumTypeService.getRawMediumTypes();
    this.mediumTypesNational = this.mediumTypes.filter(
      (type) => type.reach === "national"
    );
    this.mediumTypesLocal = this.mediumTypes.filter(
      (type) => type.reach === "local"
    );
    if (this.companyId) {
      this.company = await this.getCompany(this.companyId);
    }
    let analysis: ScoutingAnalysis | undefined;
    if (
      currentScoutingStore.scoutingAnalysis?.scouting?.id === this.analysisId
    ) {
      analysis = currentScoutingStore.scoutingAnalysis;
      analysis.scouting.scoutingMedia =
        await ScoutingMediumService.getScoutingMedia(this.analysisId);
    } else {
      analysis = await currentScoutingStore.loadScoutingAnalysis(
        this.analysisId
      );
    }
    if (analysis) {
      this.analysis = analysis;
      this.regionTotal = this.analysis?.totalRegions;
      this.districtTotal = this.analysis?.totalDistricts;
      this.townTotal = this.analysis?.totalTowns;
    }

    await this.onScoutingAnalysisStateChange();
  }

  destroyed() {
    currentScoutingStore.resetScoutingAnalysis();
  }

  addScoutingPlacesToMap() {
    if (this.googleMapsLoaded && this.analysis?.scouting.scoutingPlaces) {
      for (const place of this.analysis?.scouting.scoutingPlaces) {
        place.highlighted = true;
        this.map.addMarker(place);
      }
    }
  }
  addScoutingPlaceToMap() {
    if (this.googleMapsLoaded) {
      const place = currentScoutingStore.scoutingAnalysisPlace;
      if (place) {
        place.highlighted = true;
        this.map.addMarker(place);
      }
    }
  }

  addScoutingAreasToMap() {
    if (this.googleMapsLoaded && this.analysis?.scouting.scoutingAreas) {
      for (const area of this.analysis?.scouting.scoutingAreas) {
        area.highlighted = true;
        this.map.addArea(area);
      }
    }
  }

  addScoutingAreaToMap() {
    if (this.googleMapsLoaded) {
      const area = currentScoutingStore.scoutingAnalysisArea;
      if (area) {
        area.highlighted = true;
        this.map.addArea(area);
      }
    }
  }

  addScoutingGroupToMap() {
    if (this.googleMapsLoaded) {
      const group = currentScoutingStore.analysisCurrentScoutingGroup;
      if (group) {
        this.map.addGroup(group);
      }
    }
  }

  onMediumClick(item: MediumCardItem, group: MediumCardGroup) {
    const analysisId = this.analysis?.scouting?.id;
    const mediumTypeId = item.id;
    let sourceableId = null;
    let sourceableType = null;
    switch (currentScoutingStore.scoutingAnalysis?.status) {
      case AnalysisState.Brief:
        sourceableId = analysisId;
        sourceableType = "Scouting";
        break;
      case AnalysisState.National:
        sourceableId = analysisId;
        sourceableType = "Scouting";
        break;
      case AnalysisState.POI:
        sourceableId = currentScoutingStore.scoutingAnalysisPlace?.id;
        sourceableType = "ScoutingPlace";
        break;
      case AnalysisState.Area:
        sourceableId = currentScoutingStore.scoutingAnalysisArea?.id;
        sourceableType = "ScoutingArea";
        break;
      case AnalysisState.Group:
        sourceableId = currentScoutingStore.scoutingAnalysisGroup?.id;
        sourceableType = "ScoutingGroup";
        break;
    }

    this.$router.push({
      name: "company_analysis_medium_detail",
      params: {
        id: this.companyId,
        analysisId: analysisId || "0",
      },
      query: {
        mediumTypeId: mediumTypeId,
        sourceableId: sourceableId || "0",
        sourceableType: sourceableType || "",
      },
    });
  }
  async onDownloadClicked() {
    await new jsFileDownloader({
      url: this.exportDataUrl,
      headers: [
        { name: "Authorization", value: `Bearer ${authStore.accessToken}` },
      ],
      filename: `analisi_${currentScoutingStore.scoutingAnalysis?.scouting?.id}.xlsx`,
    });
  }

  async onReportClicked() {
    const id = currentScoutingStore.scoutingAnalysis?.scouting?.id;
    if (id) {
      this.slidesGeneration.show(id);
    }
  }

  openDeleteGroupModal(scoutingGroup: ScoutingGroup) {
    this.groupDeleteModal.show(scoutingGroup);
  }

  async onGroupDeleted(scoutingGroup: ScoutingGroup) {
    if (!this.analysis) {
      return;
    }

    const deletedGroup = await this.deleteScoutingGroup(scoutingGroup.id);
    this.analysis?.scouting.scoutingGroups?.splice(
      this.analysis?.scouting.scoutingGroups.findIndex(
        (e) => e.id === scoutingGroup.id
      ),
      1
    );

    this.analysis.scouting.scoutingMedia =
      this.analysis.scouting.scoutingMedia?.filter((e) => {
        return (
          e.sourceableType !== "ScoutingGroup" ||
          e.sourceableId.toString() !== deletedGroup.id
        );
      });

    currentScoutingStore.setScoutingAnalysis(this.analysis);

    if (
      currentScoutingStore.analysisCurrentScoutingGroup?.id &&
      deletedGroup.id === currentScoutingStore.analysisCurrentScoutingGroup.id
    ) {
      if (this.map) {
        this.map.removeGroup(deletedGroup.id);
      }
      await currentScoutingStore.clearSelection();
      await currentScoutingStore.changeScoutingAnalysisState(
        AnalysisState.Brief
      );
    }
  }
}
